import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Package, MapPin, Truck, User, Clock, AlertCircle, CheckCircle2,
  Camera, Ship, ShieldCheck, Warehouse, BoxIcon, ChevronRight, ArrowLeft,
  Phone, MessageCircle, Anchor, Globe
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HERO_BG = 'https://static.prod-images.emergentagent.com/jobs/ccdacaaf-b267-4ce7-ba28-07dd65db666f/images/f96afe67d0ac4fa17f01f143526e40a77546fa4084d2afc7c8064dc2ad3dda8c.png';

const statusTimeline = [
  { key: 'pendiente', label: 'Pendiente', icon: Clock, desc: 'Esperando recogida' },
  { key: 'recogido', label: 'Recogido', icon: Package, desc: 'Paquete recogido' },
  { key: 'en_transito', label: 'En Transito', icon: Truck, desc: 'Camino al puerto' },
  { key: 'embarque_maritimo', label: 'Embarque Maritimo', icon: Ship, desc: 'En alta mar' },
  { key: 'revision_aduana', label: 'Revision Aduana', icon: ShieldCheck, desc: 'Inspeccion aduanera' },
  { key: 'almacen_rd', label: 'Almacen RD', icon: Warehouse, desc: 'En almacen destino' },
  { key: 'en_furgon', label: 'En Furgon', icon: BoxIcon, desc: 'Cargado para entrega' },
  { key: 'camino_entrega', label: 'En Camino', icon: Truck, desc: 'En ruta de entrega' },
  { key: 'entregado', label: 'Entregado', icon: CheckCircle2, desc: 'Entrega completada' },
];

const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.08 } } };
const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } } };

const TrackingPortal = () => {
  const { numero } = useParams();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState(numero || '');
  const [trackingResult, setTrackingResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (numero) {
      setSearchQuery(numero);
      doSearch(numero);
    }
  }, [numero]);

  const doSearch = async (query) => {
    if (!query.trim()) return;
    setLoading(true);
    setError('');
    setTrackingResult(null);
    try {
      const res = await axios.get(`${API_URL}/api/shipments/tracking/${query.trim()}`);
      setTrackingResult(res.data);
    } catch (err) {
      setError(err.response?.status === 404 ? 'No se encontro envio con ese numero' : 'Error al buscar. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) navigate(`/tracking/${searchQuery.trim()}`);
  };

  const getCurrentStep = (estado) => {
    const idx = statusTimeline.findIndex(s => s.key === estado);
    return idx >= 0 ? idx : 0;
  };

  const currentStep = trackingResult ? getCurrentStep(trackingResult.estado) : -1;
  const progressPct = trackingResult ? Math.round((currentStep / (statusTimeline.length - 1)) * 100) : 0;

  const isDelivered = trackingResult?.estado === 'entregado';

  return (
    <div className="min-h-screen" style={{ fontFamily: "'DM Sans', sans-serif", background: '#F7F8FA' }}>
      {/* NAV */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-gray-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 flex items-center justify-between h-16">
          <button onClick={() => navigate('/login')} className="flex items-center gap-2 text-gray-500 hover:text-[#002D62] transition-colors" data-testid="back-to-home">
            <ArrowLeft className="h-5 w-5" />
            <span className="text-sm font-medium hidden sm:inline">Inicio</span>
          </button>
          <div className="flex items-center gap-3">
            <Anchor className="h-6 w-6 text-[#002D62]" />
            <span className="text-lg font-bold tracking-tight text-[#002D62]" style={{ fontFamily: "'Outfit', sans-serif" }}>
              DOMINICAN SHIPPING
            </span>
          </div>
          <a href="https://wa.me/12016684694" target="_blank" rel="noreferrer"
            className="flex items-center gap-1.5 bg-green-500 hover:bg-green-600 text-white text-sm font-semibold px-3 py-2 rounded-full transition-colors">
            <MessageCircle className="h-4 w-4" /> <span className="hidden sm:inline">WhatsApp</span>
          </a>
        </div>
      </nav>

      <AnimatePresence mode="wait">
        {!trackingResult && !error ? (
          /* ====== HERO / SEARCH STATE ====== */
          <motion.div key="hero" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="relative overflow-hidden" style={{ minHeight: 'calc(100vh - 64px)' }}>
            <div className="absolute inset-0">
              <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-b from-[#002D62]/80 via-[#002D62]/60 to-[#002D62]/90" />
            </div>

            <div className="relative z-10 flex flex-col items-center justify-center min-h-[calc(100vh-64px)] px-4 text-center">
              <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-2 mb-6">
                  <Globe className="h-4 w-4 text-blue-300" />
                  <span className="text-white/80 text-sm font-medium">Portal de Rastreo de Envios</span>
                </div>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-none mb-4"
                style={{ fontFamily: "'Outfit', sans-serif" }}>
                Rastrea tu Envio
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-lg sm:text-xl text-blue-100/70 max-w-lg mb-10">
                Ingresa tu numero de factura y conoce el estado de tu paquete en tiempo real
              </motion.p>

              <motion.form
                onSubmit={handleSearch}
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="w-full max-w-xl flex shadow-2xl shadow-black/30 rounded-2xl overflow-hidden">
                <div className="relative flex-1">
                  <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    data-testid="tracking-search-input"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Ej: MO-105"
                    className="w-full h-16 pl-14 pr-4 text-lg bg-white text-gray-900 placeholder:text-gray-400 outline-none"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  />
                </div>
                <button
                  data-testid="tracking-search-btn"
                  type="submit"
                  disabled={loading}
                  className="h-16 px-8 bg-[#CE1126] hover:bg-[#a80e1e] text-white text-lg font-bold flex items-center gap-2 transition-colors whitespace-nowrap">
                  {loading ? (
                    <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>Buscar <ChevronRight className="h-5 w-5" /></>
                  )}
                </button>
              </motion.form>

              {/* Quick stats */}
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="mt-16 grid grid-cols-3 gap-6 sm:gap-12 text-center">
                {[
                  { n: '500+', l: 'Envios Realizados' },
                  { n: '100+', l: 'Clientes Activos' },
                  { n: '24/7', l: 'Rastreo en Linea' },
                ].map((s, i) => (
                  <div key={i}>
                    <p className="text-2xl sm:text-3xl font-extrabold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>{s.n}</p>
                    <p className="text-xs sm:text-sm text-blue-200/50 mt-1">{s.l}</p>
                  </div>
                ))}
              </motion.div>
            </div>
          </motion.div>
        ) : error ? (
          /* ====== ERROR STATE ====== */
          <motion.div key="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="max-w-xl mx-auto px-4 py-20 text-center">
            <SearchBar query={searchQuery} setQuery={setSearchQuery} onSubmit={handleSearch} loading={loading} />
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.2 }}
              className="mt-10 bg-white rounded-3xl shadow-lg border border-red-100 p-10" data-testid="tracking-error">
              <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-5">
                <AlertCircle className="h-10 w-10 text-red-500" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Envio No Encontrado</h2>
              <p className="text-gray-500 mb-6">No pudimos encontrar un envio con el numero <strong className="text-gray-800">{numero}</strong></p>
              <p className="text-sm text-gray-400">Verifica el numero de factura e intenta de nuevo</p>
            </motion.div>
          </motion.div>
        ) : (
          /* ====== RESULTS STATE ====== */
          <motion.div key="results" variants={stagger} initial="hidden" animate="visible"
            className="max-w-7xl mx-auto px-4 sm:px-8 py-8">

            {/* Top search bar */}
            <motion.div variants={fadeUp}>
              <SearchBar query={searchQuery} setQuery={setSearchQuery} onSubmit={handleSearch} loading={loading} />
            </motion.div>

            {/* Status Hero Card */}
            <motion.div variants={fadeUp}
              className="mt-6 rounded-3xl overflow-hidden shadow-lg border border-gray-100" data-testid="tracking-result">
              <div className={`px-6 sm:px-10 py-8 sm:py-10 ${isDelivered ? 'bg-gradient-to-r from-emerald-600 to-teal-600' : 'bg-gradient-to-r from-[#002D62] to-[#003d82]'}`}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-white/50 mb-1">Factura</p>
                    <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      {trackingResult.numero_factura}
                    </h2>
                    {trackingResult.numero_furgon && (
                      <p className="text-white/50 text-sm mt-1 flex items-center gap-1.5"><Truck className="h-4 w-4" /> Furgon: {trackingResult.numero_furgon}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className={`px-5 py-2.5 rounded-full text-sm font-bold uppercase tracking-wide ${isDelivered ? 'bg-white text-emerald-700' : 'bg-white/15 text-white border border-white/20'}`}>
                      {trackingResult.estado?.replace(/_/g, ' ')}
                    </div>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="mt-8">
                  <div className="flex items-center justify-between text-xs text-white/40 mb-2">
                    <span>Pendiente</span>
                    <span>{progressPct}%</span>
                    <span>Entregado</span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${progressPct}%` }}
                      transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
                      className={`h-full rounded-full ${isDelivered ? 'bg-white' : 'bg-blue-400'}`}
                    />
                  </div>
                </div>
              </div>

              {/* Sender / Receiver */}
              <div className="bg-white grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
                <div className="p-6 sm:p-8" data-testid="sender-info-card">
                  <div className="flex items-center gap-2.5 mb-3">
                    <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
                      <User className="h-4.5 w-4.5 text-[#002D62]" />
                    </div>
                    <span className="text-xs tracking-[0.15em] uppercase font-bold text-gray-400">Remitente</span>
                  </div>
                  <p className="text-lg font-bold text-gray-900">{trackingResult.remitente?.nombre_completo || 'N/A'}</p>
                  <p className="text-sm text-gray-400 mt-0.5">{trackingResult.remitente?.ciudad || ''}</p>
                </div>
                <div className="p-6 sm:p-8" data-testid="receiver-info-card">
                  <div className="flex items-center gap-2.5 mb-3">
                    <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center">
                      <MapPin className="h-4.5 w-4.5 text-[#CE1126]" />
                    </div>
                    <span className="text-xs tracking-[0.15em] uppercase font-bold text-gray-400">Destinatario</span>
                  </div>
                  <p className="text-lg font-bold text-gray-900">{trackingResult.destinatario?.nombre_completo || 'N/A'}</p>
                  <p className="text-sm text-gray-400 mt-0.5">
                    {trackingResult.destinatario?.ciudad || ''}{trackingResult.destinatario?.provincia ? `, ${trackingResult.destinatario.provincia}` : ''}
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Bento Grid: Timeline + Details */}
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Timeline */}
              <motion.div variants={fadeUp} className="lg:col-span-2 bg-white rounded-3xl shadow-sm border border-gray-100 p-6 sm:p-8">
                <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  <Ship className="h-5 w-5 text-[#002D62]" /> Progreso del Envio
                </h3>
                <div className="relative ml-2">
                  {/* Vertical line */}
                  <div className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-gray-100" />
                  <div className="absolute left-[11px] top-2 w-0.5 bg-gradient-to-b from-emerald-500 to-blue-500 transition-all duration-1000"
                    style={{ height: `${currentStep >= 0 ? (currentStep / (statusTimeline.length - 1)) * 100 : 0}%` }} />

                  <div className="space-y-1">
                    {statusTimeline.map((step, i) => {
                      const Icon = step.icon;
                      const isCompleted = i < currentStep;
                      const isCurrent = i === currentStep;
                      const isPending = i > currentStep;
                      return (
                        <motion.div
                          key={step.key}
                          data-testid="timeline-step"
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.1 + i * 0.06, duration: 0.4 }}
                          className={`relative flex items-start gap-4 pl-10 py-3 rounded-xl transition-colors ${isCurrent ? 'bg-blue-50/60' : ''}`}>

                          {/* Node */}
                          <div className="absolute left-0 top-3.5">
                            {isCurrent && (
                              <div className="absolute -left-[3px] -top-[3px] w-[30px] h-[30px] rounded-full bg-blue-400/20 animate-pulse" />
                            )}
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 transition-all
                              ${isCompleted ? 'bg-emerald-500 border-emerald-500' : ''}
                              ${isCurrent ? 'bg-[#002D62] border-[#002D62] ring-4 ring-blue-100' : ''}
                              ${isPending ? 'bg-white border-gray-200' : ''}`}>
                              {isCompleted ? <CheckCircle2 className="h-3.5 w-3.5 text-white" /> :
                                isCurrent ? <Icon className="h-3 w-3 text-white" /> :
                                <div className="w-2 h-2 rounded-full bg-gray-200" />}
                            </div>
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-bold ${isCurrent ? 'text-[#002D62]' : isCompleted ? 'text-gray-800' : 'text-gray-300'}`}>
                              {step.label}
                            </p>
                            <p className={`text-xs mt-0.5 ${isCurrent ? 'text-blue-500' : isCompleted ? 'text-gray-400' : 'text-gray-200'}`}>
                              {step.desc}
                            </p>
                          </div>

                          {/* Status indicator */}
                          <div className="flex-shrink-0 mt-0.5">
                            {isCompleted && <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">Listo</span>}
                            {isCurrent && <span className="text-xs font-semibold text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">Actual</span>}
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              </motion.div>

              {/* Right column */}
              <div className="space-y-6">
                {/* Products */}
                {trackingResult.productos && trackingResult.productos.length > 0 && (
                  <motion.div variants={fadeUp} className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6" data-testid="product-list-card">
                    <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      <Package className="h-4 w-4 text-[#CE1126]" />
                      Articulos ({trackingResult.productos.length})
                    </h3>
                    <div className="space-y-2">
                      {trackingResult.productos.map((p, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                              <BoxIcon className="h-4 w-4 text-[#002D62]" />
                            </div>
                            <span className="text-sm font-medium text-gray-800 truncate">{p.descripcion || `Articulo ${i + 1}`}</span>
                          </div>
                          <span className="text-xs font-bold text-gray-400 bg-gray-100 px-2 py-1 rounded-md flex-shrink-0">x{p.cantidad}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Dates */}
                <motion.div variants={fadeUp} className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6">
                  <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    <Clock className="h-4 w-4 text-gray-400" /> Fechas
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400 uppercase tracking-wide font-semibold">Creado</span>
                      <span className="text-sm font-medium text-gray-700">{new Date(trackingResult.created_at).toLocaleDateString('es-DO', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    </div>
                    <div className="h-px bg-gray-100" />
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400 uppercase tracking-wide font-semibold">Actualizado</span>
                      <span className="text-sm font-medium text-gray-700">{new Date(trackingResult.updated_at).toLocaleDateString('es-DO', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    </div>
                  </div>
                </motion.div>

                {/* Photos */}
                {(trackingResult.foto_recogida || trackingResult.foto_entrega) && (
                  <motion.div variants={fadeUp} className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6">
                    <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      <Camera className="h-4 w-4 text-gray-400" /> Fotos del Envio
                    </h3>
                    <div className="space-y-3">
                      {trackingResult.foto_recogida && (
                        <div className="rounded-xl overflow-hidden border border-gray-100">
                          <div className="bg-blue-50 px-3 py-2 text-xs font-bold text-blue-700 uppercase tracking-wide">Recogida</div>
                          <img src={trackingResult.foto_recogida} alt="Recogida" className="w-full h-32 object-cover" />
                          {trackingResult.fecha_recogida && (
                            <div className="px-3 py-1.5 text-xs text-gray-400 bg-gray-50">
                              {new Date(trackingResult.fecha_recogida).toLocaleString('es-DO')}
                            </div>
                          )}
                        </div>
                      )}
                      {trackingResult.foto_entrega && (
                        <div className="rounded-xl overflow-hidden border border-gray-100">
                          <div className="bg-green-50 px-3 py-2 text-xs font-bold text-green-700 uppercase tracking-wide">Entrega</div>
                          <img src={trackingResult.foto_entrega} alt="Entrega" className="w-full h-32 object-cover" />
                          {trackingResult.fecha_entrega && (
                            <div className="px-3 py-1.5 text-xs text-gray-400 bg-gray-50">
                              {new Date(trackingResult.fecha_entrega).toLocaleString('es-DO')}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}

                {/* CTA */}
                <motion.div variants={fadeUp} className="bg-gradient-to-br from-[#002D62] to-[#003d82] rounded-3xl p-6 text-center">
                  <Phone className="h-8 w-8 text-blue-300 mx-auto mb-3" />
                  <p className="text-white font-bold text-sm mb-1">Necesitas ayuda?</p>
                  <p className="text-blue-200/60 text-xs mb-4">Contactanos por WhatsApp</p>
                  <a href="https://wa.me/12016684694" target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors">
                    <MessageCircle className="h-4 w-4" /> 201-668-4694
                  </a>
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <div className="mt-12 py-6 border-t border-gray-200 text-center">
        <p className="text-xs text-gray-400">DOMINICAN SHIPPING &middot; Envios de USA a Republica Dominicana</p>
      </div>
    </div>
  );
};

/* Extracted search bar for reuse in error/results states */
const SearchBar = ({ query, setQuery, onSubmit, loading }) => (
  <form onSubmit={onSubmit} className="flex shadow-sm rounded-2xl overflow-hidden border border-gray-200 bg-white">
    <div className="relative flex-1">
      <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-300" />
      <input
        data-testid="tracking-search-input"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Numero de factura (Ej: MO-105)"
        className="w-full h-14 pl-12 pr-4 text-base bg-transparent text-gray-900 placeholder:text-gray-400 outline-none"
        style={{ fontFamily: "'DM Sans', sans-serif" }}
      />
    </div>
    <button
      data-testid="tracking-search-btn"
      type="submit"
      disabled={loading}
      className="h-14 px-6 bg-[#CE1126] hover:bg-[#a80e1e] text-white font-bold flex items-center gap-2 transition-colors text-sm">
      {loading ? <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><Search className="h-4 w-4" /> Buscar</>}
    </button>
  </form>
);

export default TrackingPortal;
